/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';

import ErrorLevel from './error-level';

/** @type {Map<ErrorLevel, number>} */
const priorityMap = new Map(
  ['Debug', 'Log', 'Info', 'Warning', 'Error'].map((key, index) => [
    ErrorLevel[key],
    index
  ])
);

/**
 * Továrna na úrovně obecných chyb.
 */
class ErrorLevelUtil {
  /**
   * Porovná dvě úrovně chyb.
   * @param {ErrorLevel} first
   * @param {ErrorLevel} second
   * @returns {number} Číslo <0, 0, >0 dle konvencí z `Array.sort`.
   */
  static compare(first, second) {
    const firstPriority = priorityMap.get(first);
    if (isNil(firstPriority)) {
      throw new RangeError(`Unknown error level "${first}".`);
    }
    const secondPriority = priorityMap.get(second);
    if (isNil(secondPriority)) {
      throw new RangeError(`Unknown error level "${second}".`);
    }
    if (firstPriority < secondPriority) {
      return -1;
    }
    if (firstPriority > secondPriority) {
      return 1;
    }
    return 0;
  }

  // TODO: DRY ErrorWithLevel.
  /**
   * Identifikuje úroveň chyby z libovolné hodnoty.
   * Používá se k tomu speciální klíč `level`,
   * který lze nastavit libovolnému objektu.
   * @param {{ level?: any }} value
   * @returns {ErrorLevel | undefined} Identifikovaná úroveň, nebo `undefined`,
   * pokud žádná nebyla nalezena.
   */
  static fromValue(value) {
    // Pokud není hodnota objektem, tak se použije úroveň chyby dle metody.
    if (!isObject(value)) {
      return undefined;
    }
    // Z objektu lze načíst specifickou hodnotu "level".
    const valueLevel = value.level;
    // Pokud není definovaná, nebo není v číselníku,
    // tak se použije úroveň chyby dle metody.
    if (isNil(valueLevel) || !this.isErrorLevel(valueLevel)) {
      return undefined;
    }
    // Pokud se v logované hodnotě podařila nalézt úroveň, tak se použije.
    return valueLevel;
  }

  /**
   * Určí, jestli je hodnota platnou úrovní chyby.
   * @param {any} value
   * @returns {value is ErrorLevel}
   */
  static isErrorLevel(value) {
    return priorityMap.has(value);
  }
}

export default ErrorLevelUtil;
